import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { useState } from 'react';

import CookieConsent from 'react-cookie-consent';
import { toast } from 'react-toastify';
import TreedisAdminDAO from '@/api/TreedisAdminDAO';
import { allowUsesCookie, treedisCookieName } from '@/components/common/CookieConsent';
import { Props } from './types';
import { CardInputWrapper, BlueButton } from './styled';
import { initialCurrency } from '@/components/ticketingNew/static';

const inputStyle = {
  base: {
    fontSize: '16px',
    backgroundColor: '#fff',
    fontFamily: '"Heebo", sans-serif',
    fontWeight: '300',
    textIndent: '8px',
    color: 'rgb(41, 41, 41, 0.5)',
    ':-webkit-autofill': {
      color: 'rgb(41, 41, 41, 0.5)',
    },
    '::placeholder': {
      color: 'rgb(41, 41, 41, 0.5)',
    },
  },
  invalid: {
    iconColor: '#ea475c',
    color: '#ea475c',
  },
};

const cookieAcceptButton = {
  maxWidth: '382px',
  width: '100%',
  marginTop: '15px',
  height: '55px',
  boxShadow: '0 5px 4px 0 rgba(0, 0, 0, 0.18)',
  background: '#39a2db',
  color: '#FFF',
  fontSize: '16px',
  borderRadius: '5px',
};

const stripeCookieConsent = {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '15px',
};

const CheckoutForm = ({ createOrder, approveOrder, translation, settings }: Pick<Props, 'createOrder' | 'approveOrder' | 'translation' | 'settings'>) => {
  const stripe = useStripe();
  const elements = useElements();

  const [payment, setPayment] = useState({ status: 'initial' });
  const [cookiesAllowed, setCookiesAllowed] = useState(allowUsesCookie());

  const cookiesAccept = () => setCookiesAllowed(true);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!cookiesAllowed) {
      toast.error('Stripe payment will be available only after accept cookies.');
      return false;
    }
    if (!e.currentTarget.reportValidity()) return false;
    setPayment({ status: 'processing' });
    const order = await createOrder();
    if (!order) {
      setPayment({ status: 'error' });
      toast.error(`Error create order`);
      return false;
    }
    const currency = initialCurrency.find((cur) => settings.currencyId === cur.id);

    const response = await TreedisAdminDAO.createPaymentSecret({
      amount: order.totalPrice,
      currency: currency?.code?.toLowerCase(),
      metadata: {
        ticketing_order_id: order.id,
      },
      payment_method_types: ['card'],
      tourId: order.tourId,
      type: 'tickets',
    });
    setPayment(response);

    if (response.status === 500) {
      setPayment({ status: 'error' });
      toast.error(`Payment order`);
      return false;
    }

    const cardElement = elements?.getElement(CardNumberElement);

    const { error, paymentIntent } = await stripe?.confirmCardPayment(response.client_secret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: `${order.name}`,
          email: order.email,
        },
      },
    });

    if (error) {
      setPayment({ status: 'error' });
      toast.error(error.message ?? 'An unknown error occured');
    } else if (paymentIntent) {
      setPayment(paymentIntent);
      approveOrder();
    }
    return true;
  };

  return (
    <div>
      {cookiesAllowed ? (
        <form className="width-100" onSubmit={handleSubmit} id="payment-form">
          <div className="row mt-4 mb-4">
            <div className="col-md-12 input-c mb-2">
              <h3>Credit card details</h3>
            </div>
            <div className="col-md-12 input-c">
              <CardInputWrapper>
                <CardNumberElement
                  options={{
                    style: inputStyle,
                  }}
                  onChange={(e: any) => {
                    if (e.error) {
                      setPayment({ status: 'error' });
                      toast.error(e.message ?? 'An unknown error occured');
                    }
                  }}
                />
              </CardInputWrapper>
            </div>
            <div className="col-md-6 col-sm-12 input-c">
              <CardInputWrapper>
                <CardExpiryElement
                  options={{
                    style: inputStyle,
                  }}
                  onChange={(e: any) => {
                    if (e.error) {
                      setPayment({ status: 'error' });
                      toast.error(e.message ?? 'An unknown error occured');
                    }
                  }}
                />
              </CardInputWrapper>
            </div>
            <div className="col-md-6 col-sm-12 input-c">
              <CardInputWrapper>
                <CardCvcElement
                  options={{
                    style: inputStyle,
                  }}
                  onChange={(e: any) => {
                    if (e.error) {
                      setPayment({ status: 'error' });
                      toast.error(e.message ?? 'An unknown error occured');
                    }
                  }}
                />
              </CardInputWrapper>
            </div>
          </div>
          <BlueButton type="submit" className="mt-3" disabled={!['initial', 'succeeded', 'error'].includes(payment?.status) || !stripe}>
            {translation.check_out || 'Check Out'}
          </BlueButton>
        </form>
      ) : (
        <div style={stripeCookieConsent}>
          <CookieConsent
            cookieName={treedisCookieName}
            disableStyles
            debug
            buttonText="Accept cookies"
            buttonStyle={cookieAcceptButton}
            onAccept={() => {
              cookiesAccept();
            }}
          >
            Stripe payment will be available only after accept cookies.
          </CookieConsent>
        </div>
      )}
    </div>
  );
};

export default CheckoutForm;
